import React from 'react';

import LatexRenderer from '../../latex/renderer/LatexRenderer';
import '../editor/DynamicTextEditorStyle.css';
import '../../latex/renderer/latexStyle.css';
import { getImageUrl } from '@/src/utils';

type DynamicTextPreviewProps = {
    content: string;
};

export default function DynamicTextPreview({ content }: DynamicTextPreviewProps) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<root>${content}</root>`, 'text/html');
    const root = doc.body.querySelector('root') || doc.body;

    function parseStyle(styleString: string | null): React.CSSProperties {
        if (!styleString) return {};
        const style: React.CSSProperties = {};

        styleString.split(';').forEach((rule) => {
            const [prop, value] = rule.split(':').map((s) => s.trim());
            if (prop && value) {
                const camelProp = prop.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
                (style as any)[camelProp] = value;
            }
        });

        return style;
    }

    const renderNode = (node: ChildNode, index: number): React.ReactNode => {
        if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent;
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            const children = Array.from(el.childNodes).map((child, i) => renderNode(child, i));

            switch (el.tagName.toLowerCase()) {
                case 'p': {
                    // Check if paragraph is empty or only contains whitespace
                    const hasContent = children.some((child) => {
                        if (typeof child === 'string') {
                            return child.trim().length > 0;
                        }
                        return child !== null && child !== undefined;
                    });

                    return (
                        <p
                            key={index}
                            className={el.getAttribute('class') ?? undefined}
                            style={parseStyle(el.getAttribute('style') || '')}
                        >
                            {hasContent ? children : '\u00A0'}
                        </p>
                    );
                }
                case 'latex':
                    return (
                        <LatexRenderer
                            key={index}
                            latex={el.textContent ?? ''}
                            color={el.style.color || 'inherit'}
                        />
                    );
                case 'img': {
                    let src = el.getAttribute('src') || '';
                    if (src.startsWith('local://')) {
                        const guid = src.replace('local://', '');
                        src = getImageUrl(guid) || src; // fallback to original if not found
                    }

                    const parentTextAlign = el.parentElement?.style.textAlign || ''; // Check parent alignment
                    const inlineTextAlign = el.style.textAlign || ''; // Check inline alignment
                    const textAlign = inlineTextAlign || parentTextAlign; // Prioritize inline alignment

                    const alignmentStyles: React.CSSProperties = {};

                    if (textAlign === 'center') {
                        alignmentStyles.display = 'block';
                        alignmentStyles.margin = '0 auto';
                    } else if (textAlign === 'left' || textAlign === 'right') {
                        alignmentStyles.float = textAlign;
                    }

                    return (
                        <img
                            key={index}
                            src={src}
                            alt={el.getAttribute('alt') || ''}
                            height={el.getAttribute('height') || undefined}
                            width={el.getAttribute('width') || undefined}
                            style={{
                                ...alignmentStyles,
                                ...parseStyle(el.getAttribute('style') || ''),
                            }} // Apply dynamic alignment styles
                        />
                    );
                }
                case 'span': {
                    const classList = el.classList;
                    const style = parseStyle(el.getAttribute('style') || '');

                    let content: React.ReactNode = children;

                    // Apply formatting based on class names
                    if (classList.contains('dynamic-bold')) {
                        content = <strong>{content}</strong>;
                    }
                    if (classList.contains('dynamic-italic')) {
                        content = <em>{content}</em>;
                    }
                    if (classList.contains('dynamic-underline')) {
                        content = <u>{content}</u>;
                    }

                    return (
                        <span key={index} style={style}>
                            {content}
                        </span>
                    );
                }
                default:
                    return (
                        <span
                            key={index}
                            className={el.getAttribute('class') ?? undefined}
                            style={parseStyle(el.getAttribute('style') || '')}
                        >
                            {children}
                        </span>
                    );
            }
        }

        return null;
    };

    return (
        <div
            className="dynamic-preview"
            style={{ fontFamily: 'Verdana, Geneva, sans-serif', width: '100%' }}
        >
            {Array.from(root.childNodes).map((node, index) => renderNode(node, index))}
        </div>
    );
}
